import PropTypes from 'prop-types';
import { Component } from 'react';
import omit from 'lodash/omit';
import { createStructuredSelector } from 'reselect';
import { createShallowEqualSelector, createShallowMemoize } from 'app/utils/redux';

// Props that are not meant to be sent to the `calc` function
const calcPropsBlacklist = ['calc', 'width', 'height', 'children'];

/**
 * `<CalcBreakpoint>` calculates element size and props down to one
 * value of any type. Normally you would use a number but you can use
 * an object too.
 *
 * Passes calculated breakpoint to the `children` render prop.
 */
class CalcBreakpoint extends Component {
  /**
   * Calculated breakpoint can be any type. To avoid unnencessary
   * re-renders, make sure that the returned object is the same object
   * if it hasn't changed.
   *
   * @param {Function} initialProps.calc - a pure function that calculates
   * breakpoint from `(size, props)` arguments
   * @param {Function} initialProps.children - renders children, gets
   * the breakpoint value as argument
   */
  constructor(initialProps) {
    super(initialProps);

    // Strips props from unnecessary ones
    const limitProps = createShallowMemoize(props => omit(props, calcPropsBlacklist));

    // Make a size object from the width and height.
    const getSize = createStructuredSelector({
      width: (props) => props.width,
      height: (props) => props.height,
    });

    this.breakpointSelector = createShallowEqualSelector(
      (props) => props.calc,
      (props) => getSize(props),
      (props) => limitProps(props),
      (calc, size, props) => calc(size, props),
    );
  }

  render() {
    return this.props.children(this.breakpointSelector(this.props));
  }
}

CalcBreakpoint.propTypes = {
  children: PropTypes.func,
  calc:     PropTypes.func,
  height:   PropTypes.number,
  width:    PropTypes.number,
};

CalcBreakpoint.defaultProps = {
  calc:     () => undefined,
  children: () => false,
};

export default CalcBreakpoint;
